import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useLang } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { toast } from "sonner";
import { GoogleButton } from "./Login";

export const GRADES = ["Grade 8", "Grade 9", "Grade 10", "Grade 11", "Grade 12", "Undergraduate", "Postgraduate"];

export default function Signup() {
  const { t } = useLang();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", mobile: "", grade: "", school: "" });
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("form");
  const [challengeId, setChallengeId] = useState("");
  const [code, setCode] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const requestOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/api/auth/register-request", form);
      setChallengeId(data.challengeId);
      setStep("otp");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/api/auth/verify-otp", { challengeId, code });
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    try {
      await api.post("/api/auth/resend-otp", { challengeId });
      toast.success(t("otp.resent"));
      setCode("");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (step === "otp") {
    return (
      <div className="mx-auto flex max-w-md flex-col px-4 py-16" data-testid="otp-page">
        <h1 className="font-display text-3xl font-semibold sm:text-4xl" data-testid="otp-title">{t("otp.title")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{t("otp.sub", { email: form.email })}</p>
        <form onSubmit={verify} className="mt-8 space-y-6" data-testid="otp-form">
          <div className="flex justify-center">
            <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="otp-input">
              <InputOTPGroup>
                {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} />)}
              </InputOTPGroup>
            </InputOTP>
          </div>
          <Button type="submit" className="w-full" disabled={busy || code.length < 6} data-testid="otp-verify-button">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("otp.verify")}
          </Button>
        </form>
        <button onClick={resend} className="mt-5 text-center text-sm font-medium text-primary hover:underline" data-testid="otp-resend-button">
          {t("otp.resend")}
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-md flex-col px-4 py-16" data-testid="signup-page">
      <h1 className="font-display text-3xl font-semibold sm:text-4xl" data-testid="signup-title">{t("auth.signup.title")}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{t("auth.signup.sub")}</p>
      <form onSubmit={requestOtp} className="mt-8 space-y-4" data-testid="signup-form">
        <div>
          <Label htmlFor="name">{t("auth.name")}</Label>
          <Input id="name" required value={form.name} onChange={set("name")} data-testid="signup-name-input" />
        </div>
        <div>
          <Label htmlFor="email">{t("auth.email")}</Label>
          <Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="signup-email-input" />
        </div>
        <div>
          <Label htmlFor="password">{t("auth.password")}</Label>
          <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="signup-password-input" />
        </div>
        <div>
          <Label htmlFor="mobile">{t("auth.mobile")}</Label>
          <Input id="mobile" value={form.mobile} onChange={set("mobile")} data-testid="signup-mobile-input" />
        </div>
        <div>
          <Label>{t("auth.grade")}</Label>
          <Select value={form.grade} onValueChange={(v) => setForm({ ...form, grade: v })}>
            <SelectTrigger data-testid="signup-grade-select"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              {GRADES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="school">{t("auth.school")}</Label>
          <Input id="school" value={form.school} onChange={set("school")} data-testid="signup-school-input" />
        </div>
        <Button type="submit" className="w-full" disabled={busy} data-testid="signup-submit-button">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("auth.signup.btn")}
        </Button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
        <span className="h-px flex-1 bg-border" />or<span className="h-px flex-1 bg-border" />
      </div>
      <GoogleButton />
      <p className="mt-6 text-center text-sm">
        <Link to="/login" className="font-medium text-primary hover:underline" data-testid="signup-to-login">{t("auth.have.account")}</Link>
      </p>
    </div>
  );
}
